import React from 'react';
import { Link } from 'react-router-dom';
import { services } from '../mock';
import * as LucideIcons from 'lucide-react';
import { CheckCircle } from 'lucide-react';

export const Services = () => {
  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName.charAt(0).toUpperCase() + iconName.slice(1)];
    return IconComponent ? <IconComponent size={40} /> : null;
  };

  const serviceDetails = {
    1: ['Keyword research & analysis', 'On-page optimization', 'Technical SEO audits', 'Link building strategies'],
    2: ['Platform management', 'Content calendar creation', 'Community engagement', 'Paid social campaigns'],
    3: ['Blog posts & articles', 'Video production', 'Infographics', 'Brand storytelling'],
    4: ['SEO-optimized copywriting', 'Meta descriptions', 'Landing page content', 'Product descriptions'],
    5: ['Campaign strategy', 'List segmentation', 'A/B testing', 'Automation workflows'],
    6: ['Responsive design', 'UI/UX optimization', 'E-commerce solutions', 'Performance optimization']
  };

  return (
    <div className="min-h-screen" style={{ background: '#ECEC75' }}>
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-6xl font-bold text-black mb-6"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            Our Services
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto">
            Comprehensive digital marketing solutions designed to elevate your brand 
            and drive sustainable growth.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid gap-12">
            {services.map((service, index) => (
              <div 
                key={service.id}
                className={`grid md:grid-cols-2 gap-8 items-center ${
                  index % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  <div 
                    className="p-12 rounded-xl shadow-lg h-full flex flex-col justify-center"
                    style={{ background: '#e6e67c' }}
                  >
                    <div className="text-black mb-6">
                      {getIcon(service.icon)}
                    </div>
                    <h2 
                      className="text-3xl md:text-4xl font-bold text-black mb-4"
                      style={{ fontFamily: 'Crimson Text, serif' }}
                    >
                      {service.title}
                    </h2>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  <div className="bg-white p-8 rounded-xl shadow-md">
                    <h3 className="text-2xl font-bold text-black mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
                      What's Included:
                    </h3>
                    <ul className="space-y-4">
                      {serviceDetails[service.id].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="text-black flex-shrink-0 mt-1" size={20} />
                          <span className="text-gray-700 text-lg">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: '#e6e67c' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold text-black mb-4"
              style={{ fontFamily: 'Crimson Text, serif' }}
            >
              Our Process
            </h2>
            <p className="text-xl text-gray-700">
              A proven approach that delivers results
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', description: 'We learn about your business, goals, and target audience' },
              { step: '02', title: 'Strategy', description: 'We develop a custom digital marketing plan tailored to you' },
              { step: '03', title: 'Execution', description: 'Our team implements campaigns with precision and creativity' },
              { step: '04', title: 'Optimization', description: 'We continuously analyze and refine for maximum results' }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div 
                  className="text-6xl font-bold text-black mb-4 opacity-50"
                  style={{ fontFamily: 'Crimson Text, serif' }}
                >
                  {process.step}
                </div>
                <h3 className="text-2xl font-bold text-black mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                  {process.title}
                </h3>
                <p className="text-gray-700">
                  {process.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl md:text-5xl font-bold text-black mb-6"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            Let's Build Your Success Story
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            Ready to take your digital marketing to the next level? Let's talk about your goals.
          </p>
          <Link to="/contact">
            <button className="bg-black text-white px-10 py-4 rounded-lg font-semibold text-lg hover:bg-gray-800 transition-all hover:translate-y-[-2px] shadow-lg">
              Get a Free Consultation
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};